/**
 * The host's half of the compiler seam — the one place that knows a URL.
 *
 * `@qufolio/screener-grid` defines `CompileExpr` and calls it; it owns no
 * transport, because the same island runs inside a WebView on three other
 * platforms where `fetch` to a dev-server origin means nothing. So the app
 * supplies the function, exactly as it supplies `resolveToken` and `mintId`.
 *
 * Without `VITE_QUEVEE_URL` this exports `undefined`, the builder shows no text
 * input, and the structured cell works as before — a missing service degrades to
 * fewer features, never to a broken editor.
 */

import type {
  CompileExpr,
  CompileResult,
  DescribeExpr,
  DslCatalog,
} from "@qufolio/screener-grid";

import { can } from "../lib/entitlements";
import { authHeaders } from "../lib/session";

const QUEVEE_URL = import.meta.env.VITE_QUEVEE_URL ?? "";

/**
 * A network failure is returned as a compile failure rather than thrown.
 * `<ExprCell>` renders it against the input, which is where someone who just
 * pressed Enter is looking — an unhandled rejection would show nothing at all.
 */
export const compileExpr: CompileExpr | undefined = QUEVEE_URL
  ? async (source, fields): Promise<CompileResult> => {
      try {
        const response = await fetch(`${QUEVEE_URL.replace(/\/$/, "")}/dsl/compile`, {
          method: "POST",
          // quevee asks qf-us who the caller is with this token; without it
          // every call is a 401 (as it was on quevee's first day live).
          headers: { "content-type": "application/json", ...(await authHeaders()) },
          body: JSON.stringify({ source, ...(fields ? { fields: [...fields] } : {}) }),
        });
        if (!response.ok) {
          // A non-2xx means the request itself was wrong, not the expression —
          // the service answers a bad expression with 200 and `ok: false`.
          return {
            ok: false,
            error: { message: `Compiler returned ${response.status}.`, position: -1 },
          };
        }
        return (await response.json()) as CompileResult;
      } catch (cause) {
        return {
          ok: false,
          error: {
            message:
              cause instanceof Error
                ? `Could not reach the compiler: ${cause.message}`
                : "Could not reach the compiler.",
            position: -1,
          },
        };
      }
    }
  : undefined;

/**
 * The DSL vocabulary, fetched once for the editor's reference panel.
 *
 * Resolves to `undefined` when QueVee is unreachable, and the island falls back
 * to what it knows on its own — a reference listing fewer functions is better
 * than an editor that refuses to open because a lookup panel could not load.
 */
export async function fetchDslCatalog(): Promise<DslCatalog | undefined> {
  if (!QUEVEE_URL) return undefined;
  try {
    const response = await fetch(`${QUEVEE_URL.replace(/\/$/, "")}/dsl/catalog`, {
      headers: await authHeaders(),
    });
    if (!response.ok) return undefined;
    return (await response.json()) as DslCatalog;
  } catch {
    return undefined;
  }
}

/**
 * English to DSL. Costs a model call, so it is a button rather than a keystroke.
 *
 * Undefined without `VITE_QUEVEE_URL`, exactly like `compileExpr` — a host with
 * no compiler has no describer either, and the editor simply shows fewer modes.
 *
 * The plan is the *other* reason it may not be offered, and it cannot be folded
 * in here: this is a module-level const, so a capability read at load would run
 * before `/auth/me` has answered and pin the wrong answer for the session. Ask
 * `describeEnabled()` at render instead — see below.
 */
export const describeExpr: DescribeExpr | undefined = QUEVEE_URL
  ? describeAt("/dsl/describe")
  : undefined;

/**
 * The same describer, for the screener builder: every plan, its own 20-a-day
 * budget, free (owner, 2026-10-26). A different door rather than a flag, so the
 * server gates and counts it apart — see `/dsl/describe/builder` in quevee.
 */
export const describeInBuilder: DescribeExpr | undefined = QUEVEE_URL
  ? describeAt("/dsl/describe/builder")
  : undefined;

/** Whether the builder offers ✦ Describe: `describeEnabled()`'s rule, its capability. */
export function builderDescribeEnabled(): boolean {
  return QUEVEE_URL !== "" && can("feat:describeBuilder");
}

function describeAt(path: string): DescribeExpr {
  return async (request, columns, current): Promise<CompileResult> => {
      try {
        const response = await fetch(`${QUEVEE_URL.replace(/\/$/, "")}${path}`, {
          method: "POST",
          // quevee asks qf-us who the caller is with this token; without it
          // every call is a 401 (as it was on quevee's first day live).
          headers: { "content-type": "application/json", ...(await authHeaders()) },
          body: JSON.stringify({
            request,
            ...(columns ? { columns } : {}),
            ...(current ? { current } : {}),
          }),
        });
        if (!response.ok) {
          // The service's own sentence where it sent one — "daily limit
          // reached (20 per day)" says what to do; a status code does not.
          const detail = await response
            .json()
            .then((body: { detail?: unknown }) => (typeof body.detail === "string" ? body.detail : ""))
            .catch(() => "");
          return {
            ok: false,
            error: { message: detail ? `Describe: ${detail}.` : `Describe returned ${response.status}.`, position: -1 },
          };
        }
        return (await response.json()) as CompileResult;
      } catch (cause) {
        return {
          ok: false,
          error: {
            message:
              cause instanceof Error
                ? `Could not reach QueVee: ${cause.message}`
                : "Could not reach QueVee.",
            position: -1,
          },
        };
      }
  };
}

/**
 * Whether the describe control should be offered at all.
 *
 * `journalEnabled()`'s shape, and for its reason: two causes — no service
 * configured, or a plan without `feat:describe` — that mean the same thing to
 * every call site, so they are answered together rather than as a second
 * condition wherever the control is drawn.
 *
 * **A function, not a const**, unlike `describeExpr` beside it. Entitlements
 * arrive with `/auth/me` and this module is imported long before that, so the
 * answer has to be asked for rather than captured. Permissive until it lands.
 *
 * The daily budget (`lim:describeCallsDay`) is deliberately *not* here. Running
 * out is a different thing from not being sold it — the control stays, and the
 * service says what happened. Hiding it on exhaustion would read as the feature
 * being withdrawn.
 */
export function describeEnabled(): boolean {
  return QUEVEE_URL !== "" && can("feat:describe");
}
